import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/works.css';
import { allArtworksQuery } from '../lib/queries';
import { useSanityPreview } from '../lib/useSanityPreview';

interface Artwork {
  _id: string;
  title: string;
  artist: string;
  slug: string;
  year: number;
  medium: string;
  imageUrl: string;
  artistImageUrl?: string;
  price: number;
  edition: {
    size: number;
    available: number;
  };
};

const Works: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: artworks, error, isLoading } = useSanityPreview<Artwork[]>(allArtworksQuery);
  const [showEmail, setShowEmail] = React.useState(false);

  // Find the index of the artwork with the given slug
  const currentIndex = React.useMemo(() => {
    if (!artworks || !slug) return 0;
    const idx = artworks.findIndex(a => a.slug === slug);
    return idx === -1 ? 0 : idx;
  }, [artworks, slug]);

  // If the slug is missing or invalid, redirect to the first artwork
  useEffect(() => {
    if (artworks && artworks.length > 0) {
      if (!slug || artworks.findIndex(a => a.slug === slug) === -1) {
        navigate(`/works/${artworks[0].slug}`, { replace: true });
      }
    }
  }, [artworks, slug, navigate]);

  // Navigation functions update the URL
  const goToPrevious = () => {
    if (!artworks || artworks.length === 0) return;
    const prevIndex = (currentIndex - 1 + artworks.length) % artworks.length;
    navigate(`/works/${artworks[prevIndex].slug}`);
  };
  const goToNext = () => {
    if (!artworks || artworks.length === 0) return;
    const nextIndex = (currentIndex + 1) % artworks.length;
    navigate(`/works/${artworks[nextIndex].slug}`);
  };


  // Loading state
  if (isLoading) {
    return <div className="w-full h-screen flex items-center justify-center">Loading artworks...</div>;
  }

  // Error state
  if (error) {
    return <div className="w-full h-screen flex items-center justify-center">Error loading artworks: {error.message}</div>;
  }

  // No artworks state
  if (!artworks || artworks.length === 0) {
    return <div className="w-full h-screen flex items-center justify-center">No artworks found.</div>;
  }


  // Get current artwork safely
  const currentArtwork = artworks[currentIndex];
  if (!currentArtwork) {
    console.error('Current artwork is undefined!');
    return <div>Error loading artwork</div>;
  }

  console.log('Rendering artwork:', currentArtwork.title);

  return (
    <div className="works-page">
      <div className="artwork-page">
        {/* Artwork image with navigation overlays */}
        <div className="artwork-image-section" style={{ overflow: 'hidden' }}>
          <div className="artwork-image-wrapper" style={{ boxShadow: 'none' }}>
            <div style={{ position: 'relative', width: '100%' }}>
              {/* Left overlay for previous with subtle indicator */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '50%',
                  height: '100%',
                  zIndex: 10,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  background: 'rgba(255,255,255,0.001)' // Almost invisible but clickable
                }}
                onClick={goToPrevious}
                title="Previous artwork"
                className="artwork-nav-left"
                onMouseEnter={(e) => {
                  // Find the indicator and make it visible on hover
                  const indicator = e.currentTarget.querySelector('div');
                  if (indicator) {
                    indicator.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  // Find the indicator and hide it on leave
                  const indicator = e.currentTarget.querySelector('div');
                  if (indicator) {
                    indicator.style.opacity = '0';
                  }
                }}
              >
                {/* Simple triangular left arrow - only visible on hover */}
                <div style={{
                  width: '15px',
                  height: '15px',
                  marginLeft: '30px',
                  position: 'relative',
                  opacity: '0',
                  transition: 'opacity 0.5s ease'
                }}>
                  <div style={{
                    width: 0, 
                    height: 0,
                    borderTop: '7px solid transparent',
                    borderRight: '12px solid rgba(0,0,0,0.25)',
                    borderBottom: '7px solid transparent',
                  }}></div>
                </div>
              </div>

              {/* Right overlay for next with subtle indicator */}
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  top: 0,
                  width: '50%',
                  height: '100%',
                  zIndex: 10,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  background: 'rgba(255,255,255,0.001)' // Almost invisible but clickable
                }}
                onClick={goToNext}
                title="Next artwork"
                className="artwork-nav-right"
                onMouseEnter={(e) => {
                  // Find the indicator and make it visible on hover
                  const indicator = e.currentTarget.querySelector('div');
                  if (indicator) {
                    indicator.style.opacity = '1';
                  }
                }}
                onMouseLeave={(e) => {
                  // Find the indicator and hide it on leave
                  const indicator = e.currentTarget.querySelector('div');
                  if (indicator) {
                    indicator.style.opacity = '0';
                  }
                }}
              >
                {/* Simple triangular right arrow - only visible on hover */}
                <div style={{
                  width: '15px',
                  height: '15px',
                  marginRight: '30px',
                  position: 'relative',
                  opacity: '0',
                  transition: 'opacity 0.5s ease'
                }}>
                  <div style={{
                    width: 0, 
                    height: 0,
                    borderTop: '7px solid transparent',
                    borderLeft: '12px solid rgba(0,0,0,0.25)',
                    borderBottom: '7px solid transparent',
                  }}></div>
                </div>
              </div>

              {/* The artwork image */}
              <img
                src={currentArtwork.imageUrl}
                alt={currentArtwork.title}
                className="artwork-image"
                style={{ width: '100%', display: 'block', position: 'relative', zIndex: 1 }}
              />
              {/* Watch Film button for lets-keep-it-to-ourself */}
              {currentArtwork.slug === 'lets-keep-it-to-ourself' && (
                <WatchFilmOverlay />
              )}
            </div>
          </div>
        </div>

        {/* Artwork details */}
        <div className="artwork-details-container" style={{ border: 'none', borderRadius: '0' }}>
          <div className="artwork-details-handle" style={{ overflow: 'hidden' }}>
            <div className="handle-indicator" style={{ boxShadow: 'none' }}></div>
          </div>
          <div className="artwork-details-content">
            <h1 className="artwork-title font-ogg font-light italic">{currentArtwork.title}</h1>
            
            <div className="artist-info">
              {currentArtwork.artistImageUrl && (
                <img
                  src={currentArtwork.artistImageUrl}
                  alt={currentArtwork.artist}
                  className="artist-image"
                />
              )}
              <span className="artist-name font-book-antiqua font-bold">{currentArtwork.artist}</span>
            </div>
            
            <div className="artwork-metadata">
              <div className="metadata-item">
                <span className="metadata-label font-book-antiqua">Year</span>
                <span className="metadata-value">{currentArtwork.year}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label font-book-antiqua">Medium</span>
                <span className="metadata-value">{currentArtwork.medium}</span>
              </div>
              <div className="metadata-item">
                <span className="metadata-label">Available</span>
                <span className="metadata-value">
                  {currentArtwork.edition && currentArtwork.edition.available > 0 ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            
            {/* Inquire button that shows email and triggers email client */}
            <div className="inquire-section" style={{ marginTop: '1.5rem' }}>
              <button
                className="inquire-btn"
                style={{
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.5rem',
                  background: '#222',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.1rem'
                }}
                onClick={() => {
                  // Toggle email visibility
                  setShowEmail(!showEmail);
                  
                  // Open email client
                  window.location.href = `mailto:contact@substans.art?subject=Artwork Inquiry: ${currentArtwork.title}`;
                }}
              >
                Inquire
              </button>
              
              {/* Show email address when button is clicked */}
              {showEmail && (
                <div style={{ marginTop: '0.75rem', fontSize: '1.1rem', color: '#222' }}>
                  contact@substans.art
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// WatchFilmOverlay component for the film modal and button
const WatchFilmOverlay: React.FC = () => {
  const [showFilm, setShowFilm] = React.useState(false);
  return (
    <>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 30,
        }}
      >
        <button
          onClick={e => {
            e.stopPropagation(); // Prevent navigation
            setShowFilm(true);
          }}
          style={{
            background: 'rgba(0,0,0,0.75)',
            color: 'white',
            padding: '1rem 2rem',
            borderRadius: '2rem',
            fontSize: '1.2rem',
            border: 'none',
            cursor: 'pointer',
            fontWeight: 600,
            boxShadow: '0 4px 32px rgba(0,0,0,0.2)',
            outline: 'none',
          }}
        >
          ▶
        </button>
      </div>
      {showFilm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100,
          background: 'rgba(0,0,0,0.92)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ position: 'relative', width: '90vw', maxWidth: 900 }}>
            <button
              onClick={() => setShowFilm(false)}
              style={{
                position: 'absolute',
                top: 10,
                right: 10,
                color: 'white',
                fontSize: 36,
                fontWeight: 700,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                zIndex: 101,
              }}
              aria-label="Close film modal"
            >
              ×
            </button>
            <iframe
              src="https://player.vimeo.com/video/1088883061?title=0&amp;byline=0&amp;portrait=0&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479"
              width="100%"
              height="500"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              title="Let's Keep It To Ourself"
              style={{ width: '100%', borderRadius: 12, boxShadow: '0 4px 32px rgba(0,0,0,0.5)' }}
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}
    </>
  );
};

export default Works;
